import { z } from "zod";

export * from "./auth.js";
export * from "./authCookie.js";
export * from "./manifest.js";
export * from "./proof.js";
export * from "./discovery.js";
export * from "./watchlist.js";
export * from "./serverPlane.js";
export * from "./broker.js";
export * from "./shares.js";
export * from "./claimRequests.js";
export * from "./directZone.js";
export * from "./watchSync.js";
export * from "./trakt.js";

/**
 * RFC-7807-style problem details — the error shape every Rago API returns
 * (ARCHITECTURE.md §3). `code` is the machine-readable discriminator clients
 * switch on; `type`/`title`/`detail` are for humans and logs.
 */
export interface ProblemDetails {
  type: string;
  title: string;
  status: number;
  detail?: string;
  code: ErrorCode;
  /** Extension member: the request field that failed validation. */
  field?: string;
  /** Extension member of POST /v1/connections' 409: the connection the pair
   * already has, so the client can say connected / invited / invited-me. */
  connection?: { id: string; status: "pending" | "accepted"; direction: "sent" | "received" };
}

export const ErrorCodes = {
  // generic
  internal: "internal",
  not_found: "not_found",
  validation_failed: "validation_failed",
  conflict: "conflict",
  rate_limited: "rate_limited",
  unauthorized: "unauthorized",
  forbidden: "forbidden",
  // auth (used from M1 on)
  invalid_credentials: "invalid_credentials",
  mfa_required: "mfa_required",
  proof_invalid: "proof_invalid",
  token_expired: "token_expired",
  device_revoked: "device_revoked",
  email_not_verified: "email_not_verified",
  // sign-up and Settings › Account: the 409's `field` names the input
  username_taken: "username_taken",
  email_taken: "email_taken",
  // an email change to the address the account already has
  email_unchanged: "email_unchanged",
  // pins / claim (M2)
  pin_expired: "pin_expired",
  pin_not_approved: "pin_not_approved",
  // a forwardUrl pin approved from a different public IP than the one that created it
  pin_origin_mismatch: "pin_origin_mismatch",
  // a forwardUrl pin naming a server the account has no live grant on (approve, redemption)
  server_access_denied: "server_access_denied",
  claim_token_invalid: "claim_token_invalid",
  // setup wizard claim requests (Plex-style /setup flow; Go mirrors these)
  claim_request_expired: "claim_request_expired",
  already_claimed: "already_claimed",
  claim_in_flight: "claim_in_flight",
  cloud_unreachable: "cloud_unreachable",
  // server plane (rago-server build)
  assertion_invalid: "assertion_invalid",
  // the server's row no longer exists (removed from its account) — the
  // server treats this exact code as the self-unclaim signal
  server_not_registered: "server_not_registered",
  csr_invalid: "csr_invalid",
  cert_issuance_failed: "cert_issuance_failed",
  cert_unavailable: "cert_unavailable",
  relay_unavailable: "relay_unavailable",
  local_exchange_denied: "local_exchange_denied",
  // upstream / metadata (Rago Discovery)
  bad_gateway: "bad_gateway",
  // provider protocol (rago-metadata; §4d — Go mirrors this)
  upstream_failed: "upstream_failed",
  // transcode engine (rago-server Phase 4; Go mirrors these)
  decision_expired: "decision_expired",
  transcode_limit: "transcode_limit",
  transcode_disabled: "transcode_disabled",
  audio_unavailable: "audio_unavailable",
  media_deletion_disabled: "media_deletion_disabled",
  // through the relay, a file or a session above the relay's quality limit
  relay_limit: "relay_limit",
  // Downloads (rago-server /v1/downloads).
  downloads_disabled: "downloads_disabled",
  download_not_ready: "download_not_ready",
  download_gone: "download_gone",
  no_space: "no_space",
  // Settings › Trakt: no Trakt app configured on the broker, or Trakt did not answer
  trakt_unavailable: "trakt_unavailable",
  // connecting Trakt while "Sync my watch state" is off
  watch_sync_required: "watch_sync_required",
} as const;

export type ErrorCode = (typeof ErrorCodes)[keyof typeof ErrorCodes];

/** List envelope for collection responses (ARCHITECTURE.md §3). */
export const listMetaSchema = z.object({
  offset: z.number().int().min(0),
  limit: z.number().int().min(0),
  total: z.number().int().min(0),
});

export function listEnvelopeSchema<T extends z.ZodType>(item: T) {
  return z.object({ meta: listMetaSchema, items: z.array(item) });
}

/** /healthz — liveness (no dependencies consulted). */
export const healthResponseSchema = z.object({
  status: z.literal("ok"),
  service: z.string(),
  version: z.string(),
});
export type HealthResponse = z.infer<typeof healthResponseSchema>;

/** /readyz — readiness (DB reachable). */
export const readyResponseSchema = z.object({
  status: z.enum(["ok", "degraded"]),
  db: z.boolean(),
});
export type ReadyResponse = z.infer<typeof readyResponseSchema>;
